import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import "./Campaign.css";
import {
  getCampaignReferralCount,
  getCampaignReferralPatients,
  searchCampaignReferralPatients,
} from "../../api/admin/dashboard";
import PaginationComponent from "../AllComponents/Shared/PaginationComponent";

const CONTACTED_STATUS_LABELS = {
  maybe: "May be",
  not_converted: "Not interested",
  interested: "Interested",
};

const formatContactedStatus = (value) => {
  if (!value) return "—";
  if (CONTACTED_STATUS_LABELS[value]) return CONTACTED_STATUS_LABELS[value];
  return String(value).replace(/_/g, " ");
};

const COLUMN_COUNT = 8;

const Campaign = () => {
  const [search, setSearch] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [patients, setPatients] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const limitOptions = [10, 20, 30, 50, 100];
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNext: false,
    hasPrev: false,
  });
  const searchTimeoutRef = useRef(null);

  const isSearching = Boolean(search.trim() || campaignName.trim());
  const filtered = isSearching && !searchLoading ? searchResults : patients;
  const totalItems = isSearching
    ? pagination.totalCount || filtered.length
    : totalCount;

  const fetchCount = async () => {
    try {
      const { data } = await getCampaignReferralCount();
      if (data?.success) {
        setTotalCount(data.data?.campaignReferralCount || 0);
      }
    } catch (err) {
      console.error("Failed to fetch campaign count", err);
    }
  };

  const fetchPatients = async (pageNum = page) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getCampaignReferralPatients(pageNum, limit);

      if (data?.success) {
        const patientsList = data.data?.patients || [];
        setPatients(patientsList);

        if (data.data) {
          setPagination({
            currentPage: data.data.page || pageNum,
            totalPages: data.data.totalPages || 1,
            totalCount: data.data.count || 0,
            limit,
            hasNext: (data.data.page || pageNum) < (data.data.totalPages || 1),
            hasPrev: (data.data.page || pageNum) > 1,
          });
          setTotalCount(data.data.count || 0);
        }
      }
      setError(null);
    } catch (err) {
      console.error("Failed to load campaign patients", err);
      setError("Failed to load campaign patients. Please try again.");
      setPatients([]);
      toast.error("Failed to load campaign patients. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCount();
  }, []);

  useEffect(() => {
    if (!isSearching) {
      fetchPatients(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  useEffect(() => {
    setPage(1);
  }, [search, campaignName, limit]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const query = search.trim();
    const campaign = campaignName.trim();

    if (!query && !campaign) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }

    setSearchLoading(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const { data } = await searchCampaignReferralPatients(campaign, query, page, limit);

        if (data?.success) {
          setSearchResults(data.data?.patients || []);
          if (data.data) {
            setPagination({
              currentPage: data.data.page || page,
              totalPages: data.data.totalPages || 1,
              totalCount: data.data.count || 0,
              limit,
              hasNext: (data.data.page || page) < (data.data.totalPages || 1),
              hasPrev: (data.data.page || page) > 1,
            });
          }
        }
      } catch (err) {
        console.error("Failed to search campaign patients", err);
        setSearchResults([]);
        toast.error("Failed to search. Please try again.");
      } finally {
        setSearchLoading(false);
      }
    }, 800);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search, campaignName, page, limit]);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return "—";
    try {
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } catch (e) {
      return "—";
    }
  };

  return (
    <div className="camp-surface">
      <div className="camp-shell">
        <header className="camp-hero">
          <div className="camp-hero-copy">
            <p className="camp-chip">Campaign referrals</p>
            <h1>
              Patient campaign data
              <span> at your fingertips.</span>
            </h1>
          </div>
        </header>

        <section className="camp-search-block">
          <div className="camp-search-field">
            <svg className="camp-search-icon" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
            <input
              type="text"
              placeholder="Search by name, phone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="camp-search-field">
            <svg className="camp-search-icon" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
            <input
              type="text"
              placeholder="Filter by campaign name…"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
            />
          </div>
          <span className="camp-count">
            {isSearching
              ? `Showing ${filtered.length} of ${totalItems} patients`
              : `All campaign patients (${totalItems})`}
          </span>
        </section>

        <section className="camp-table-wrapper">
          <table className="camp-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Age</th>
                <th>Gender</th>
                <th>Contact</th>
                <th>Campaign</th>
                <th>Sales Status</th>
                <th>Customer Response</th>
                <th>Registered</th>
              </tr>
            </thead>
            <tbody>
              {loading || searchLoading ? (
                Array.from({ length: Math.min(limit, 8) }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="camp-skeleton-row">
                    {Array.from({ length: COLUMN_COUNT }).map((__, cell) => (
                      <td key={cell}>
                        <div className="camp-skeleton-cell" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="camp-empty">
                    <div className="camp-empty-inner">
                      <strong>Failed to load patients</strong>
                      <p>{error}</p>
                      <button type="button" className="camp-retry" onClick={() => fetchPatients()}>
                        Retry
                      </button>
                    </div>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="camp-empty">
                    <div className="camp-empty-inner">
                      <strong>
                        {isSearching
                          ? "No patients match your search"
                          : "No campaign patients found"}
                      </strong>
                      {!isSearching && (
                        <p>Patients registered through campaigns will appear here.</p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((patient) => {
                  const patientId = patient._id || patient.id;
                  const status = (patient.sales_status || "pending").toLowerCase();
                  const isContacted = status === "contacted";
                  const gender = (patient.gender || "").toLowerCase();
                  return (
                    <tr key={patientId}>
                      <td className="camp-name">{patient.name || "N/A"}</td>
                      <td className="camp-nowrap">
                        {patient.age !== undefined && patient.age !== null && patient.age !== ""
                          ? patient.age
                          : calculateAge(patient.dob)}
                      </td>
                      <td>
                        <span
                          className={`camp-badge camp-gender ${
                            gender === "male" ? "male" : gender === "female" ? "female" : "unknown"
                          }`}
                        >
                          {patient.gender || "N/A"}
                        </span>
                      </td>
                      <td className="camp-nowrap">{patient.contact || "—"}</td>
                      <td className="camp-campaign-cell">
                        <span className="camp-badge camp-campaign" title={patient.campaign_name || ""}>
                          {patient.campaign_name || "—"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`camp-badge camp-status ${
                            isContacted ? "contacted" : status === "converted" ? "converted" : "pending"
                          }`}
                        >
                          {patient.sales_status || "pending"}
                        </span>
                      </td>
                      <td>
                        {isContacted ? (
                          <span
                            className={`camp-badge camp-response ${patient.contacted_status || "unknown"}`}
                          >
                            {formatContactedStatus(patient.contacted_status)}
                          </span>
                        ) : (
                          <span className="camp-muted">—</span>
                        )}
                      </td>
                      <td className="camp-nowrap">{formatDate(patient.createdAt)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </section>

        {!loading &&
          !error &&
          !searchLoading &&
          totalItems > limit &&
          (isSearching || pagination.totalPages > 1) && (
            <PaginationComponent
              total={totalItems}
              limit={limit}
              limitOptions={limitOptions}
              activePage={page}
              onChangePage={(newPage) => setPage(newPage)}
              onChangeLimit={(nextLimit) => {
                setLimit(nextLimit);
                setPage(1);
              }}
              className="camp-pagination"
            />
          )}
      </div>
    </div>
  );
};

export default Campaign;
